/**
 * Normalized entity models for the asset domain (TanStack DB rows).
 *
 * Conventions that hold across every model in this file:
 * - `assetId` is the legacy `"<address>:<chainId>"` form; CAIP-style inputs
 *   are normalized down to it (see {@link normalizeAssetDomainAssetIds}).
 * - `updatedAt` is the client ingestion time (Date.now(), epoch ms), not a
 *   server timestamp.
 * - The asset-universe models form the multi-issuer graph:
 *   canonical (underlying real-world asset, e.g. AAPL) → product (an
 *   issuer's offering of it, e.g. Ondo's tokenized AAPL) → route (a concrete
 *   onchain token on one chain). Symbol links index tickers back to
 *   canonicals.
 */
import { tryAssetIdLikeToLegacyAssetId } from "@repo/caip";
import type { OndoMarketDataExtension } from "@repo/market-data/ondo";
import type {
  CanonicalSectorClassification,
  RwaCanonicalTradingStatus,
  RwaIssuedProductTradingStatus,
  RwaTokenSubjectRelationType,
} from "@repo/ports/v2";
import type {
  AssetEquityDetails,
  AssetTaxonomySummary,
} from "@repo/services/facades/assets.facade";
import { normalizeAssetIdFormat } from "@repo/types";

/** Market-data provider namespace for extension rows; only Ondo today. */
export type AssetProvider = "ondo";

/**
 * Effective identity + display metadata for one onchain token (base metadata
 * with server-side overrides already applied). Keyed by asset:
 * `id` === `assetId`. The server response is authoritative — requested ids it
 * omits are deleted from the collection, not preserved.
 */
export interface AssetModel {
  address: string;
  assetId: string;
  chainId: number;
  decimals: number | null;
  id: string;
  imageUrl: string | null;
  name: string;
  symbol: string;
  updatedAt: number;
}

/**
 * Realtime market/price snapshot for one asset, sourced from Defined.fi
 * (Codex). Keyed by asset: `id` === `assetId`. Numeric suffixes on field
 * names are trailing-window hours (1 = trailing 1h, 24 = trailing 24h).
 */
export interface AssetPriceModel {
  assetId: string;
  /** Buy trades in the trailing 1 hour. */
  buyCount1: number | null;
  /** Price change over the trailing 1h, percent (0-100 scale). */
  change1Percent: number | null;
  /** Price change over the trailing 24h, percent (0-100 scale). */
  change24Percent: number | null;
  /** Price × circulating supply, USD. */
  circulatingMarketCap: number | null;
  /** High price over the trailing 1h, USD. */
  high1Usd: number | null;
  id: string;
  /** Pool liquidity, USD. */
  liquidity: number | null;
  /**
   * Display market cap — usually FDV (price × total supply); tokenized
   * equities may override with the underlying issuer's market cap.
   */
  marketCap: number | null;
  priceUsd: number | null;
  /** Which side of the trading pair the price is quoted against. */
  quoteToken: string | null;
  /** Total transactions in the trailing 1 hour. */
  txnCount1: number | null;
  /** Unique-wallet transactions in the trailing 1 hour. */
  uniqueTransactions1: number | null;
  updatedAt: number;
  /** Trade volume in the trailing 1h, USD. */
  volume1: number | null;
  /** Trade volume in the trailing 24h, USD. */
  volume24: number | null;
}

/**
 * Classification/exposure summary for one asset: issuer, sector/category
 * exposures with weights, and the tokenized-equity flag. Keyed by asset:
 * `id` === `assetId`.
 */
export type AssetTaxonomyModel = AssetTaxonomySummary & {
  assetId: string;
  id: string;
  updatedAt: number;
};

/**
 * Rich equity/RWA detail for one asset: identity (issuer/ticker/underlying),
 * market snapshots (including sharesMultiplier), dividend income, access
 * (networks, payment methods, trading sessions), and documents. Keyed by
 * asset: `id` === `assetId`. Only populated from the overlay/asset-details
 * path — browse/stocks list responses do not carry it.
 */
export type AssetEquityDetailsModel = AssetEquityDetails & {
  assetId: string;
  id: string;
  updatedAt: number;
};

/**
 * Ondo Global Markets extension for one asset: market-hours/session state
 * (current/scheduled session, isMarketOpen, isAssetTradeable, pauseReason),
 * per-session trading limits, 24h underlying volume, sparkline, and tags.
 * `id` = `"ondo:<assetId>"`.
 */
export interface AssetOndoProviderExtensionModel {
  assetId: string;
  extension: OndoMarketDataExtension;
  id: string;
  provider: "ondo";
  updatedAt: number;
}

/** Union of provider extension rows; a single member until more providers land. */
export type AssetProviderExtensionModel = AssetOndoProviderExtensionModel;

/**
 * The canonical underlying real-world asset (e.g. AAPL the equity, BTC) —
 * root node of the multi-issuer graph: one canonical → many issuer products
 * → many onchain routes. `id` = `"canonical:<assetCanonicalId>"`.
 */
export interface AssetUniverseCanonicalModel {
  assetCanonicalId: string;
  assetClass: string | null;
  coingeckoId: string | null;
  countryCode: string | null;
  currencyCode: string | null;
  /** Derivative markets (perps/futures venues) referencing this underlying. */
  derivativeMarketCount: number;
  /** Canonical is tradeable/supported by Glider (drives availability filters). */
  gliderSupported: boolean;
  id: string;
  imageUrl: string | null;
  instrumentType: string | null;
  /** Onchain assets issued across this canonical's products. */
  issuedOnchainAssetCount: number;
  /** Distinct issuer products offering this underlying. */
  issuerProductCount: number;
  /** Starts null; merged in later from CoinGecko market quotes. */
  marketCapRank: number | null;
  marketCapUsd: number | null;
  /** Raw canonical metadata bag from the server. */
  metadata: Record<string, unknown>;
  name: string;
  /** Starts null; merged in later from CoinGecko market quotes. */
  priceChangePercent1h: number | null;
  priceChangePercent24h: number | null;
  priceUsd: number | null;
  primaryExchange: string | null;
  sicDescription: string | null;
  /** Internal display-sector taxonomy (11 sectors + Unclassified). */
  sectorClassification: CanonicalSectorClassification | null;
  /** Glider-supported subset of `issuedOnchainAssetCount`. */
  supportedIssuedOnchainAssetCount: number;
  /** Glider-supported subset of `issuerProductCount`. */
  supportedIssuerProductCount: number;
  symbol: string;
  /** Server-authoritative permission to bind this ticker to a live stream. */
  streamSymbolUnique: boolean | null;
  /** Starts null; merged in later from CoinGecko market quotes. */
  totalValueLockedUsd: number | null;
  /** Token representations linked to this canonical. */
  tokenRepresentationCount: number;
  /** Starts null; merged in later from CoinGecko market quotes. */
  totalVolumeUsd: number | null;
  /** Underlying stock-market session status (exchange open/close, after hours). */
  tradingStatus: RwaCanonicalTradingStatus | null;
  updatedAt: number;
}

/**
 * One server-paired tokenized-equity valuation leg. The factor and share
 * price are sampled together; refused/flagged legs may intentionally omit
 * either. `id` = `"<assetId>:<canonicalId>"`.
 */
export interface AssetTokenizedEquityValuationModel {
  assetDbId: number;
  assetId: string;
  canonicalId: string;
  decimals?: number;
  id: string;
  isGliderSupported: boolean;
  issuerSlug: string | null;
  legStatus: "priced" | "refused" | "flagged";
  marketDate?: string;
  mechanism?: string;
  pairedAt?: number;
  provisional: "verified" | "provisional" | "unknown";
  /** Canonical graph request that produced this row, including conflict rows. */
  requestedCanonicalId: string;
  refusalReason?: string;
  sharePriceUsd?: string;
  sharesPerToken?: string;
}

/**
 * Explicit completeness marker for one canonical's issuer-product and route
 * graph. Summary hydration intentionally does not create this row; a full
 * canonical-group response does, including when the complete graph is empty.
 * `id` = `"graph-hydration:<assetCanonicalId>"`.
 */
export interface AssetUniverseGraphHydrationModel {
  assetCanonicalId: string;
  graphRows: "complete";
  id: string;
  schemaVersion: 1;
  updatedAt: number;
  /** Present only after a graph response carried the valuation envelope. */
  valuationRows?: "complete";
  /** Availability slice whose response established valuation completeness. */
  valuationAvailabilityFilter?: "indexed" | "glider_supported";
  /** Freshness clock for valuation rows; identity refreshes never advance it. */
  valuationUpdatedAt?: number;
}

/**
 * An issuer's product/offering of a canonical asset (e.g. Ondo's tokenized
 * AAPL) — the middle tier between canonical and onchain routes.
 * `id` = `"product:<productId>"`.
 */
export interface AssetUniverseProductModel {
  assetCanonicalId: string;
  /** Networks where the product is quotable via the Bebop RFQ integration. */
  bebopRfqNetworks: string[];
  /** Falls back issuerSymbol → canonical name/symbol. */
  displayName: string;
  id: string;
  imageUrl: string | null;
  isBebopRfqSupported: boolean;
  /** Product is Glider-tradeable. */
  isGliderSupported: boolean;
  issuerName: string | null;
  issuerSlug: string | null;
  issuerSymbol: string;
  /** Raw product metadata bag from the server. */
  metadata: Record<string, unknown>;
  priceChangePercent24h: number | null;
  priceUsd: number | null;
  /** Numeric server-side product id (also the row key). */
  productId: number;
  productSlug: string;
  productType: string;
  /** How the product's token relates to the underlying (issuer_representation, wrapped_token, …). */
  relationshipType: RwaTokenSubjectRelationType;
  /** Concrete onchain asset ids (legacy format) of this product's routes. */
  routeAssetIds: string[];
  routeCount: number;
  /** Upstream data-source name (e.g. "coingecko"). */
  source: string;
  /** Product id in the upstream `source` system. */
  sourceProductId: string;
  /** Issuer session/market-hours status (isMarketOpen, tradable sessions, …). */
  tradingStatus: RwaIssuedProductTradingStatus | null;
  updatedAt: number;
}

/**
 * A concrete onchain token on a specific chain — the tradeable leaf of the
 * universe graph. Two id schemes: product-issued routes use
 * `"route:product:<productId>:<assetId>"`; token-representation routes use
 * `"route:token:<assetCanonicalId>:<relationshipType>:<assetId>"` (these
 * have `productId` null and all Bebop fields false/null).
 */
export interface AssetUniverseRouteModel {
  /** Numeric server DB id of the asset row, when known. */
  assetDbId: number | null;
  assetCanonicalId: string;
  /** Legacy `"<address>:<chainId>"` id of the concrete token. */
  assetId: string;
  /** Token contract reference (address) on its chain. */
  assetReference: string;
  bebopRfqCanBuy?: boolean;
  bebopRfqCanSell?: boolean;
  bebopRfqNetwork: string | null;
  /** Why Bebop RFQ support is limited/absent, when provided. */
  bebopRfqReason?: string | null;
  /** CAIP-2 namespace, e.g. "eip155". */
  chainNamespace: string;
  /** CAIP-2 chain reference, e.g. "1". */
  chainReference: string;
  decimals: number | null;
  id: string;
  isBebopRfqSupported: boolean;
  isGliderSupported: boolean;
  /** Owning product; null for token-representation routes. */
  productId: number | null;
  /** How this token relates to the canonical underlying (wrapped, bridged, …). */
  relationshipType: RwaTokenSubjectRelationType;
  /** Product-asset link role (e.g. underlying vs receipt); null for token routes. */
  role: string | null;
  /** Upstream source of the link/asset. */
  source: string;
  symbol: string;
  updatedAt: number;
}

/**
 * Symbol → canonical lookup entry (resolve a ticker like "AAPL" to its
 * `assetCanonicalId`, scoped by asset class).
 * `id` = `"symbol:<assetClass|any>:<SYMBOL>"` (class lowercased, symbol
 * uppercased).
 */
export interface AssetUniverseSymbolLinkModel {
  assetCanonicalId: string;
  assetClass: string | null;
  id: string;
  /**
   * Which universe layer produced the symbol; on collision canonical wins
   * over product, which wins over route.
   */
  source: "canonical" | "product" | "route";
  symbol: string;
  updatedAt: number;
}

/**
 * Per-asset geo/compliance restriction status (mirrors the backend
 * country/region token-restriction blocklists). Note: no normalizer in this
 * feature currently populates it — it exists as an optional pass-through
 * input to the stocks view-model.
 */
export interface AssetPolicyModel {
  assetId: string;
  id: string;
  isRestricted: boolean;
  /** Human-readable restriction reason shown to the user, when restricted. */
  message: string | null;
  updatedAt: number;
}

/**
 * Does this id name an addressable token, as opposed to a grouping handle or a
 * search key?
 *
 * Callers legitimately hold ids that are neither: `"canonical:<uuid>"` for a
 * canonical share rollup, `"<portfolioId>:concrete:<id>"` for a dashboard
 * summary row, bare tickers for symbol lookups, blueprint references for
 * strategy templates. None of those can be served by the asset batch
 * endpoints — the server parses the leading segment as an address and rejects
 * the whole request — so anything heading for those endpoints has to be
 * filtered through here first.
 *
 * Deliberately NOT applied inside {@link normalizeAssetDomainAssetIds}: that
 * one also normalizes hook inputs and cache keys, where non-asset search keys
 * are expected to survive untouched.
 */
export function isConcreteAssetDomainAssetId(assetId: string): boolean {
  return /^[^:\s]+:\d+$/.test(assetId.trim());
}

/**
 * Normalize a mixed batch of asset ids to the legacy `"<address>:<chainId>"`
 * form (CAIP inputs converted down), deduped and sorted for stable
 * query/collection keys.
 */
export function normalizeAssetDomainAssetIds(
  assetIds: readonly string[],
): string[] {
  const seen = new Set<string>();
  const normalizedAssetIds: string[] = [];

  for (const assetId of assetIds) {
    const trimmed = assetId.trim();
    if (trimmed.length === 0) {
      continue;
    }

    let normalized = tryAssetIdLikeToLegacyAssetId(trimmed) ?? trimmed;
    try {
      normalized = normalizeAssetIdFormat(normalized);
    } catch {
      normalized = tryAssetIdLikeToLegacyAssetId(trimmed) ?? trimmed;
    }

    if (seen.has(normalized)) {
      continue;
    }

    seen.add(normalized);
    normalizedAssetIds.push(normalized);
  }

  return normalizedAssetIds.sort((left, right) => left.localeCompare(right));
}

/** Identity today (`id` === `assetId`); exists to make the keying explicit. */
export function toAssetDomainCollectionId(assetId: string): string {
  return assetId;
}

/** Provider-extension row key: `"<provider>:<assetId>"`. */
export function toAssetProviderExtensionId(
  assetId: string,
  provider: AssetProvider,
): string {
  return `${provider}:${assetId}`;
}

/** Canonical row key: `"canonical:<assetCanonicalId>"`. */
export function toAssetUniverseCanonicalId(assetCanonicalId: string): string {
  return `canonical:${assetCanonicalId}`;
}

/** Paired valuation row key: `"<assetId>:<canonicalId>"`. */
export function toAssetTokenizedEquityValuationId(input: {
  assetId: string;
  canonicalId: string;
}): string {
  return `${input.assetId}:${input.canonicalId}`;
}

/** Complete-graph hydration row key: `"graph-hydration:<assetCanonicalId>"`. */
export function toAssetUniverseGraphHydrationId(
  assetCanonicalId: string,
): string {
  return `graph-hydration:${assetCanonicalId}`;
}

/** Product row key: `"product:<productId>"`. */
export function toAssetUniverseProductId(productId: number): string {
  return `product:${productId}`;
}

/** Product-issued route row key: `"route:product:<productId>:<assetId>"`. */
export function toAssetUniverseProductRouteId(input: {
  assetId: string;
  productId: number;
}): string {
  return `route:product:${input.productId}:${input.assetId}`;
}

/**
 * Token-representation route row key:
 * `"route:token:<assetCanonicalId>:<relationshipType>:<assetId>"`.
 */
export function toAssetUniverseTokenRouteId(input: {
  assetCanonicalId: string;
  assetId: string;
  relationshipType: RwaTokenSubjectRelationType;
}): string {
  return `route:token:${input.assetCanonicalId}:${input.relationshipType}:${input.assetId}`;
}

/** Symbol-link row key: `"symbol:<assetClass|any>:<SYMBOL>"`. */
export function toAssetUniverseSymbolLinkId(
  symbol: string,
  assetClass?: string | null,
): string {
  const normalizedClass = assetClass?.trim().toLowerCase() || "any";
  return `symbol:${normalizedClass}:${symbol.trim().toUpperCase()}`;
}
